import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { TFunction } from 'i18next'
import { scheduleSync } from '../../../lib/sync/sync-engine'
import type { Project, UpdateProject } from '../../../lib/db/models'

interface UseProjectHeaderParams {
  project: Project | null | undefined
  updateProject: (updates: Partial<UpdateProject>) => Promise<Project | null>
  projectId: string | undefined
  activeTab: { id: string } | null | undefined
  updateTab: (id: string, data: { title: string }) => void
  closeActiveTab: () => void
  setBreadcrumbTitle: (title: string | null) => void
  navFocusFirstRef: React.RefObject<(() => void) | null>
  t: TFunction
  /** True when the owning ProjectPage is the active tab. Inactive pages
   *  stay mounted under `display: none`; the breadcrumb observer must
   *  stay silent for them or the project title leaks into every tab's
   *  breadcrumb. */
  isActive?: boolean
}

export function useProjectHeader({
  project,
  updateProject,
  projectId,
  activeTab,
  updateTab,
  closeActiveTab,
  setBreadcrumbTitle,
  navFocusFirstRef,
  t,
  isActive = true,
}: UseProjectHeaderParams) {
  const [name, setName] = useState('')
  const [savedName, setSavedName] = useState('')
  const [notesValue, setNotesValue] = useState('')
  const [savedNotes, setSavedNotes] = useState('')
  const [isEditingHeader, setIsEditingHeader] = useState(false)

  const titleRef = useRef<HTMLTextAreaElement>(null)
  const notesRef = useRef<HTMLTextAreaElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const hasInitialized = useRef(false)
  const prevProjectIdRef = useRef(projectId)

  // Detect projectId change — only reset the init flag so the new project's
  // data gets picked up by the useLayoutEffect below. DON'T clear name/notes
  // here: the old values must stay in the DOM so the crossfade ghost (created
  // in ProjectPage's useLayoutEffect) captures the correct old content.
  // The real page is hidden (opacity: 0) anyway, so stale values are invisible.
  if (projectId !== prevProjectIdRef.current) {
    prevProjectIdRef.current = projectId
    hasInitialized.current = false
    if (isEditingHeader) setIsEditingHeader(false)
  }

  // Initialize name + notes from project data.
  // Guard against stale useLiveQuery results: only init when project.id matches projectId.
  // useLayoutEffect so the title is set BEFORE the page fade-in (which also uses useLayoutEffect).
  useLayoutEffect(() => {
    if (project && !hasInitialized.current && project.id === projectId) {
      setName(project.name)
      setSavedName(project.name)
      setNotesValue(project.description ?? '')
      setSavedNotes(project.description ?? '')
      hasInitialized.current = true

      // Ensure tab shows default name for unnamed projects
      if (!project.name && activeTab) {
        updateTab(activeTab.id, { title: t('project.defaultName') })
      }

      // Auto-focus if project has no name (just created)
      if (!project.name) {
        const focusTimer = setTimeout(() => titleRef.current?.focus(), 0)
        return () => clearTimeout(focusTimer)
      }
    }
  }, [project, projectId])

  // Close tab when project is deleted (e.g. from another device via sync).
  useEffect(() => {
    if (project?.deletedAt) {
      queueMicrotask(() => closeActiveTab())
    }
  }, [project?.deletedAt, closeActiveTab])

  // Sync external name changes (e.g. from another device) into local state and tab title
  useEffect(() => {
    if (!project || !hasInitialized.current) return
    if (project.name === savedName) return

    if (document.activeElement !== titleRef.current) {
      setName(project.name)
      setSavedName(project.name)

      if (activeTab) {
        updateTab(activeTab.id, { title: project.name || t('project.defaultName') })
      }
    }
  }, [project?.name])

  // Sync external description changes into local notes state
  useEffect(() => {
    if (!project || !hasInitialized.current) return
    const remote = project.description ?? ''
    if (remote === savedNotes) return

    if (document.activeElement !== notesRef.current) {
      setNotesValue(remote)
      setSavedNotes(remote)
    }
  }, [project?.description])

  // Auto-resize textareas when value changes externally (init, sync, revert)
  useEffect(() => {
    if (titleRef.current) {
      titleRef.current.style.height = 'auto'
      titleRef.current.style.height = `${titleRef.current.scrollHeight}px`
    }
  }, [name])

  useEffect(() => {
    if (notesRef.current) {
      notesRef.current.style.height = 'auto'
      notesRef.current.style.height = `${notesRef.current.scrollHeight}px`
    }
  }, [notesValue, isEditingHeader])

  // Show project title in breadcrumb when it scrolls out of view.
  // Read savedName via ref to keep the observer stable across title changes.
  const savedNameRef = useRef(savedName)
  savedNameRef.current = savedName

  useEffect(() => {
    // Inactive tabs stay mounted via TabStack (display: none). Their title
    // element registers as "not intersecting" to an IntersectionObserver —
    // which would falsely claim the project's name should be shown in the
    // breadcrumb, leaking the project title into every other tab's chrome.
    // Only run the observer while this ProjectPage is the active tab.
    if (!isActive) {
      setBreadcrumbTitle(null)
      return
    }
    const titleEl = titleRef.current
    if (!titleEl) return

    const scrollContainer = titleEl.closest('.tab-stack__slot-scroll, .main-layout__scroll-wrap')
    if (!scrollContainer) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setBreadcrumbTitle(entry.isIntersecting ? null : savedNameRef.current || null)
      },
      { root: scrollContainer, threshold: 0, rootMargin: '-16px 0px 0px 0px' },
    )

    observer.observe(titleEl)
    return () => {
      observer.disconnect()
      // When the page deactivates we might still be leaving behind the
      // project title we last pushed; clear it so the next active tab
      // starts from a blank breadcrumb.
      setBreadcrumbTitle(null)
    }
  }, [isActive, savedName, setBreadcrumbTitle])

  // Clean up breadcrumb title on unmount
  useEffect(() => {
    return () => setBreadcrumbTitle(null)
  }, [setBreadcrumbTitle])

  const handleSave = useCallback(async () => {
    const trimmed = name.trim()
    if (trimmed === savedName) return

    // Empty input: revert to saved name instead of committing default
    if (!trimmed) {
      setName(savedName)
      if (activeTab) {
        updateTab(activeTab.id, { title: savedName || t('project.defaultName') })
      }
      return
    }

    await updateProject({ name: trimmed })
    setName(trimmed)
    setSavedName(trimmed)

    // Update the tab title directly via context
    if (activeTab) {
      updateTab(activeTab.id, { title: trimmed })
    }

    // Trigger immediate sync after rename
    scheduleSync()
  }, [name, savedName, updateProject, t, activeTab, updateTab])

  const handleNotesSave = useCallback(async () => {
    const trimmed = notesValue.trim()
    if (trimmed === savedNotes) return

    await updateProject({ description: trimmed || null })
    setNotesValue(trimmed)
    setSavedNotes(trimmed)
    scheduleSync()
  }, [notesValue, savedNotes, updateProject])

  const handleRevert = useCallback(() => {
    setName(savedName)
    setNotesValue(savedNotes)
    setIsEditingHeader(false)
    titleRef.current?.blur()
    notesRef.current?.blur()
  }, [savedName, savedNotes])

  // Blur handler for the header container — tracks focus within title+notes
  const handleHeaderBlur = useCallback((e: React.FocusEvent) => {
    if (headerRef.current?.contains(e.relatedTarget as Node)) return
    handleSave()
    handleNotesSave()
    setIsEditingHeader(false)
  }, [handleSave, handleNotesSave])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSave()
      handleNotesSave()
      setIsEditingHeader(false)
      titleRef.current?.blur()
      notesRef.current?.blur()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      handleRevert()
    }
    if (e.key === 'ArrowDown' && titleRef.current?.selectionStart === titleRef.current?.value.length) {
      e.preventDefault()
      if (notesRef.current) {
        notesRef.current.focus()
      } else {
        titleRef.current?.blur()
        navFocusFirstRef.current?.()
      }
    }
  }, [handleSave, handleNotesSave, handleRevert])

  const handleNotesKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSave()
      handleNotesSave()
      setIsEditingHeader(false)
      titleRef.current?.blur()
      notesRef.current?.blur()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      handleRevert()
    }
    if (e.key === 'ArrowUp' && notesRef.current?.selectionStart === 0) {
      e.preventDefault()
      titleRef.current?.focus()
    }
    if (e.key === 'ArrowDown' && notesRef.current?.selectionStart === notesRef.current?.value.length) {
      e.preventDefault()
      notesRef.current?.blur()
      navFocusFirstRef.current?.()
    }
  }, [handleSave, handleNotesSave, handleRevert])

  return {
    name,
    setName,
    savedName,
    notesValue,
    setNotesValue,
    savedNotes,
    isEditingHeader,
    setIsEditingHeader,
    titleRef,
    notesRef,
    headerRef,
    handleKeyDown,
    handleNotesKeyDown,
    handleHeaderBlur,
  }
}
